import React, { useEffect, useMemo, useRef, useState } from "react";
import { setSessionExpired, useMainController } from "../../context";
import { httpMushPro } from "../../services/http-common";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import SimpleModal from "./SimpleModal";

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
interface ContinuousCalendarProps {
  onClick?: (_day: number, _month: number, _year: number) => void;
}

export const ContinuousCalendar: React.FC<ContinuousCalendarProps> = ({
  onClick,
}) => {
  const today = new Date();
  const dispatch = useMainController();
  const [eventsByDate, setEventsByDate] = useState<Record<string, any[]>>({});
  const dayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(today.getMonth());
  const monthOptions = monthNames.map((month, index) => ({
    name: month,
    value: `${index}`,
  }));
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const scrollToDay = (monthIndex: number, dayIndex: number) => {
    const targetDayIndex = dayRefs.current.findIndex(
      (ref) =>
        ref &&
        ref.getAttribute("data-month") === `${monthIndex}` &&
        ref.getAttribute("data-day") === `${dayIndex}`
    );

    const targetElement = dayRefs.current[targetDayIndex];

    if (targetDayIndex !== -1 && targetElement) {
      const container = document.querySelector(".calendar-container");
      const elementRect = targetElement.getBoundingClientRect();
      const is2xl = window.matchMedia("(min-width: 1536px)").matches;
      const offsetFactor = is2xl ? 3 : 2.5;

      if (container) {
        const containerRect = container.getBoundingClientRect();
        const offset =
          elementRect.top -
          containerRect.top -
          containerRect.height / offsetFactor +
          elementRect.height / 2;

        container.scrollTo({
          top: container.scrollTop + offset,
          behavior: "smooth",
        });
      } else {
        const offset =
          window.scrollY +
          elementRect.top -
          window.innerHeight / offsetFactor +
          elementRect.height / 2;

        window.scrollTo({
          top: offset,
          behavior: "smooth",
        });
      }
    }
  };

  const handlePrevYear = () => setYear((prevYear) => prevYear - 1);
  const handleNextYear = () => setYear((prevYear) => prevYear + 1);

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const monthIndex = parseInt(event.target.value, 10);
    setSelectedMonth(monthIndex);
    scrollToDay(monthIndex, 1);
  };

  const handleTodayClick = () => {
    setYear(today.getFullYear());
    setSelectedMonth(today.getMonth());
    scrollToDay(today.getMonth(), today.getDate());
  };

  const handleDayClick = (
    day: number,
    month: number,
    year: number,
    event?: any
  ) => {
    if (!onClick) return;
    if (event) {
      setSelectedEvent(event);
      setModalOpen(true);
    }
    onClick(day, month, year);
  };

  // Helper to get 5-week grid for a given month
  function getMonthGrid(year: number, month: number) {
    const firstDayOfMonth = new Date(year, month, 1);
    const startDayOfWeek = firstDayOfMonth.getDay(); // 0 (Sun) - 6 (Sat)
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Previous month
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevMonthYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();

    const days: { day: number; month: number; year: number }[] = [];

    // Fill days from previous month
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        month: prevMonth,
        year: prevMonthYear,
      });
    }

    // Fill current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, month, year });
    }

    // Calculate total cells needed: 35 (5 weeks) or 42 (6 weeks)
    const totalCells = days.length > 35 ? 42 : 35;

    // Fill next month to complete totalCells days
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextMonthYear = month === 11 ? year + 1 : year;
    let nextDay = 1;
    while (days.length < totalCells) {
      days.push({
        day: nextDay++,
        month: nextMonth,
        year: nextMonthYear,
      });
    }

    return days;
  }

  // Only render the selected month as a 5-week grid
  const generateCalendar = useMemo(() => {
    const today = new Date();
    const calendarDays = getMonthGrid(year, selectedMonth);

    const calendarWeeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      calendarWeeks.push(calendarDays.slice(i, i + 7));
    }

    return calendarWeeks.map((week, weekIndex) => (
      <div className="flex w-full" key={`week-${weekIndex}`}>
        {week.map(({ month, day, year: cellYear }, dayIndex) => {
          const index = weekIndex * 7 + dayIndex;
          const isNewMonth =
            index === 0 ||
            (calendarDays[index - 1] &&
              calendarDays[index - 1].month !== month);
          const isToday =
            today.getMonth() === month &&
            today.getDate() === day &&
            today.getFullYear() === cellYear;
          const isCurrentMonth = month === selectedMonth && cellYear === year;

          // Get events for this day
          const dateKey = new Date(cellYear, month, day)
            .toISOString()
            .slice(0, 10);
          const events = eventsByDate[dateKey] || [];

          return (
            <div
              key={`${month}-${day}-${cellYear}`}
              ref={(el) => {
                dayRefs.current[index] = el;
              }}
              data-month={month}
              data-day={day}
              // Removed onClick from here
              className={`relative z-10 m-[-0.5px] group aspect-square w-full grow rounded-xl border font-medium transition-all hover:z-20 hover:border-[#000080] sm:-m-px sm:size-20 sm:rounded-md sm:border-2 lg:size-36 lg:rounded-xl 2xl:size-40 ${
                !isCurrentMonth ? "bg-slate-50" : ""
              }`}
            >
              <span
                className={`absolute left-1 top-1 flex size-5 items-center justify-center rounded-full text-xs sm:size-6 sm:text-sm lg:left-2 lg:top-2 lg:size-8 lg:text-base ${
                  isToday ? "bg-blue-500 px-1 font-semibold text-white" : ""
                } ${!isCurrentMonth ? "text-slate-400" : "text-slate-800"}`}
              >
                {day}
              </span>

              {isNewMonth && (
                <span
                  className=" absolute text-[#cbd5e1] bottom-1 left-0 w-full truncate px-1.5 text-xs font-semibold text-slate-300    sm:bottom-0 sm:text-sm     lg:bottom-2.5 lg:left-3.5 lg:w-auto lg:max-w-[90%] lg:px-0 lg:text-sm    2xl:mb-[-4px] 2xl:text-base    whitespace-nowrap    overflow-hidden    text-ellipsis"
                  style={{
                    maxWidth: "100%",
                    minWidth: 0,
                  }}
                >
                  {monthNames[month]}
                </span>
              )}

              <div className="absolute inset-x-0 top-7 flex flex-col items-center gap-0.5 px-1 sm:top-8 lg:top-10">
                {events.length > 0 ? (
                  events.map((event, idx) => (
                    <span
                      key={idx}
                      className="w-full truncate rounded-md bg-green-100 px-1 text-xs font-semibold text-green-900 sm:text-xs cursor-pointer hover:bg-green-200"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent bubbling to parent
                        handleDayClick(day, month, cellYear, event); // Pass the event object
                      }}
                    >
                      {event.title || "Event"}
                    </span>
                  ))
                ) : (
                  <span className="w-full truncate rounded-md bg-gray-100 px-1 text-xs font-medium text-gray-400 sm:text-xs">
                    {/* Empty or default */}
                  </span>
                )}
              </div>
              {/* ...existing button code... */}
              <button
                type="button"
                className="absolute right-2 top-2 rounded-full opacity-0 transition-all focus:opacity-100 group-hover:opacity-100"
              >
                <svg
                  className="size-8 scale-90 text-blue-500 transition-all hover:scale-100 group-focus:scale-100"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm11-4.243a1 1 0 1 0-2 0V11H7.757a1 1 0 1 0 0 2H11v3.243a1 1 0 1 0 2 0V13h3.243a1 1 0 1 0 0-2H13V7.757Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    ));
  }, [year, selectedMonth, eventsByDate]);

  useEffect(() => {
    const calendarContainer = document.querySelector(".calendar-container");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const month = parseInt(
              entry.target.getAttribute("data-month")!,
              10
            );
            setSelectedMonth(month);
          }
        });
      },
      {
        root: calendarContainer,
        rootMargin: "-75% 0px -25% 0px",
        threshold: 0,
      }
    );

    dayRefs.current.forEach((ref) => {
      if (ref && ref.getAttribute("data-day") === "15") {
        observer.observe(ref);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  function restructureEvents(events: any[]) {
    const eventMap: Record<string, any[]> = {};
    events.forEach((event) => {
      let dateStr = event.date;
      if (!dateStr && event.startDate) dateStr = event.startDate;
      if (!dateStr) return;
      const dateObj = new Date(dateStr);
      if (isNaN(dateObj.getTime())) return;
      const key = dateObj.toISOString().slice(0, 10);
      if (!eventMap[key]) eventMap[key] = [];
      eventMap[key].push(event);
    });
    console.log(eventMap);
    return eventMap;
  }

  const getEvents = useMutation(
    () => {
      return httpMushPro.get(
        "user/scheduling/events",
        null,
        setSessionExpired,
        dispatch
      );
    },
    {
      onSuccess: ({ data }) => {
        const newEvents = restructureEvents(data.events);
        setEventsByDate(newEvents);
      },
      onError: (error: any) => {
        toast.error(error.response.data.message);
      },
    }
  );
  useEffect(() => {
    getEvents.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div className="no-scrollbar calendar-container max-h-full overflow-y-scroll rounded-t-2xl bg-white pb-10 text-slate-800 shadow-xl">
        <div className="sticky -top-px z-50 w-full rounded-t-2xl bg-white">
          <div className="mb-4 flex w-full flex-wrap items-center justify-between gap-6">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <Select
                name="month"
                value={`${selectedMonth}`}
                options={monthOptions}
                onChange={handleMonthChange}
              />
              <button
                onClick={handleTodayClick}
                type="button"
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-100 lg:px-5 lg:py-2.5"
              >
                Today
              </button>
            </div>
            <div className="flex w-fit items-center justify-between">
              <button
                onClick={handlePrevYear}
                className="rounded-full border border-slate-300 p-1 transition-colors hover:bg-slate-100 sm:p-2"
              >
                <svg
                  className="size-5 text-slate-800"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m15 19-7-7 7-7"
                  />
                </svg>
              </button>
              <h1 className="min-w-16 text-center text-lg font-semibold sm:min-w-20 sm:text-xl">
                {year}
              </h1>
              <button
                onClick={handleNextYear}
                className="rounded-full border border-slate-300 p-1 transition-colors hover:bg-slate-100 sm:p-2"
              >
                <svg
                  className="size-5 text-slate-800"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m9 5 7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className="w-full px-5 pt-4 sm:px-8 sm:pt-6">
          <div className="grid w-full grid-cols-7 justify-between text-slate-500 mb-2">
            {daysOfWeek.map((day, index) => (
              <div
                key={index}
                className="w-full border-b border-slate-200 py-1 text-center font-semibold text-xs sm:py-2 sm:text-sm lg:py-3 lg:text-base 2xl:text-lg"
              >
                {day}
              </div>
            ))}
          </div>{" "}
          {generateCalendar}
        </div>
      </div>
      <SimpleModal open={modalOpen} onClose={() => setModalOpen(false)}>
        {selectedEvent ? (
          <div>
            <h2 className="text-lg font-bold mb-2">
              {selectedEvent.title || "Untitled Event"}
            </h2>
            <p className="mb-1">
              <span className="font-semibold">Start Date:</span>{" "}
              {selectedEvent.slot.startTime || "-"}
            </p>
            <p className="mb-1">
              <span className="font-semibold">End Date:</span>{" "}
              {selectedEvent.slot.endTime || "-"}
            </p>
            <p className="mb-1">
              <span className="font-semibold">Description:</span>{" "}
              {selectedEvent.description || "No description"}
            </p>
            <p className="mb-1">
              <span className="font-semibold">Location:</span>{" "}
              {selectedEvent.location || "-"}
            </p>
            <p className="mb-1">
              <span className="font-semibold">Meeting Link:</span>{" "}
              {selectedEvent.meetingLink ? (
                <a
                  href={selectedEvent.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  {selectedEvent.meetingLink}
                </a>
              ) : (
                "-"
              )}
            </p>
            <p className="mb-1">
              <span className="font-semibold">Timezone:</span>{" "}
              {selectedEvent.timezone || "-"}
            </p>
            <div className="mb-1">
              <span className="font-semibold">Attendees:</span>
              <ul className="list-disc pl-5">
                {selectedEvent.attendees.map(
                  (
                    attendee: { email: string; confirmed: boolean },
                    idx: number
                  ) => (
                    <li key={attendee.email || idx}>
                      <span>{attendee.email}</span>
                      <span
                        className={`ml-2 text-xs ${
                          attendee.confirmed
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        {attendee.confirmed ? "Confirmed" : "Not confirmed"}
                      </span>
                    </li>
                  )
                )}
                {(!selectedEvent.attendees ||
                  selectedEvent.attendees.length === 0) && (
                  <li className="text-gray-400">No attendees</li>
                )}
              </ul>
            </div>
          </div>
        ) : (
          <div>No event selected.</div>
        )}
      </SimpleModal>
    </>
  );
};

export interface SelectProps {
  name: string;
  value: string;
  label?: string;
  options: { name: string; value: string }[];
  onChange: (_event: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
}

export const Select = ({
  name,
  value,
  label,
  options = [],
  onChange,
  className,
}: SelectProps) => (
  <div className={`relative ${className}`}>
    {label && (
      <label htmlFor={name} className="mb-2 block font-medium text-slate-800">
        {label}
      </label>
    )}
    <select
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      className="cursor-pointer rounded-lg border border-gray-300 bg-white py-1.5 pl-2 pr-6 text-sm font-medium text-gray-900 hover:bg-gray-100 sm:rounded-xl sm:py-2.5 sm:pl-3 sm:pr-8"
      required
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.name}
        </option>
      ))}
    </select>
    <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-1 sm:pr-2">
      <svg
        className="size-5 text-slate-600"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </span>
  </div>
);
